/* eslint-disable react/prop-types */
import dayjs from "dayjs";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import WorkerMiniProfile from "../../../../components/WorkerMiniProfile/WorkerMiniProfile";
import { mediaUrl } from "../../../../helpers/mediaHelper";
import ShiftSection from "../components/ShiftSection";
import WorkerDateCheckboxes from "../components/WorkerDateCheckboxes";
import {
  getStep1ActionKey,
  getWorkerDisplayName,
  getWorkerSlug,
  getWorkerAvailableDates,
} from "./useStep1SuggestedWorkers";

function isSuggestedWorkerAlreadyInvited(worker, shiftInvite) {
  if (
    worker?.alreadyInvited === true ||
    worker?.workerAlreadyInvited === true
  ) {
    return true;
  }
  const slug = getWorkerSlug(worker);
  if (!slug || !shiftInvite?.workers?.length) return false;
  const entry = shiftInvite.workers.find((x) => x.workerSlug === slug);
  if (entry) return true;
  return entry?.alreadyInvited === true;
}

function InviteListPanel({
  shiftIndex,
  shiftInvite,
  shiftCap,
  shiftCurrentCount,
  onRemove,
  removeDisabled,
  pendingRemoveKey,
  inviteListLoading,
}) {
  if (inviteListLoading) {
    return (
      <div className="border rounded p-3 mb-3 bg-light d-flex align-items-center gap-2">
        <Spinner size="sm" />
        <span className="text-muted small">Loading invite list…</span>
      </div>
    );
  }

  const workers = shiftInvite?.workers ?? [];
  const workerAlreadyInvited = !!shiftInvite?.workerAlreadyInvited;

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="fw-semibold mb-2">Invite list</div>
      {shiftCap > 0 && (
        <p className="text-muted small mb-2">
          Capacity: {shiftCurrentCount}/{shiftCap}
        </p>
      )}
      {workerAlreadyInvited && (
        <Alert color="info" className="py-2 mb-2">
          Invites already sent for this shift.
        </Alert>
      )}
      {!workers.length ? (
        <p className="text-muted small mb-0">
          No workers on your invite list for this shift yet.
        </p>
      ) : (
        <ul className="list-unstyled mb-0">
          {workers.map((w) => {
            const rowKey = getStep1ActionKey(shiftIndex, w.workerSlug);
            const removeRowPending = pendingRemoveKey === rowKey;
            return (
              <li
                key={w.workerSlug}
                className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom"
              >
                <div>
                  <span className="fw-medium">{w.workerSlug}</span>
                  {Array.isArray(w.dates) && w.dates.length > 0 && (
                    <div className="small text-muted">
                      {w.dates
                        .map((d) => dayjs(d).format("MMM D, YYYY"))
                        .join(" · ")}
                    </div>
                  )}
                </div>
                <Button
                  color="link"
                  size="sm"
                  className="text-danger p-0"
                  disabled={
                    removeDisabled || w.alreadyInvited || workerAlreadyInvited
                  }
                  onClick={() => onRemove(w.workerSlug)}
                >
                  {removeRowPending ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Removing…
                    </>
                  ) : (
                    "Remove"
                  )}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function WorkerRow({
  worker,
  selectedDates,
  onToggleDate,
  onSelectAllDatesForWorker,
  onAddToInviteList,
  onReject,
  addDisabled,
  addPending,
  rejectDisabled,
  rejectPending,
  idPrefix,
  onViewProfile,
  showReject = true,
}) {
  const slug = getWorkerSlug(worker);
  const name = getWorkerDisplayName(worker);
  const dates = getWorkerAvailableDates(worker);
  const photoRaw =
    worker?.personalInformation?.uploadSelfie || worker?.user?.photo || worker?.photo;
  const photoSrc = photoRaw ? mediaUrl(photoRaw) : null;
  const rating = worker?.rating ?? worker?.averageRating;
  const distanceKm = worker?.distanceKm ?? worker?.distance;

  return (
    <div className="border rounded mb-3 overflow-hidden bg-white">
      <div className="px-3 py-3 border-bottom">
        <Row className="align-items-start g-3">
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div>
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt=""
                    className="rounded-circle"
                    style={{ width: 40, height: 40, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                  >
                    <i
                      className="mdi mdi-account text-muted"
                      style={{ fontSize: 24 }}
                    />
                  </div>
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="fw-semibold">{name}</span>
                {rating != null && (
                  <Badge color="light" className="text-dark">
                    <i className="mdi mdi-star text-warning me-1" />
                    {Number(rating).toFixed(1)}
                  </Badge>
                )}
                {distanceKm != null && (
                  <span className="text-muted small">
                    {Number(distanceKm).toFixed(1)} km
                  </span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12}>
            <div className="small fw-semibold mb-1">Available dates</div>
            <WorkerDateCheckboxes
              dates={dates}
              selectedDates={selectedDates}
              onToggleDate={(iso) => onToggleDate(iso)}
              idPrefix={idPrefix}
            />
            <Button
              color="light"
              size="sm"
              className="mt-2"
              disabled={!dates.length}
              onClick={() => onSelectAllDatesForWorker()}
            >
              Select all dates
            </Button>
          </Col>
          <Col
            xs={12}
            className={`w-full d-flex gap-2 align-items-center align-items-md-end ${
              showReject ? "" : "flex-wrap"
            }`}
          >
            <Button
              color="primary"
              outline
              className={showReject ? "flex-grow-1" : "w-100"}
              disabled={addDisabled}
              onClick={() =>
                slug && onAddToInviteList(slug, [...selectedDates])
              }
            >
              {addPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Adding…
                </>
              ) : (
                "Add to invite list"
              )}
            </Button>
            {showReject ? (
              <Button
                color="danger"
                outline
                className="flex-grow-1"
                disabled={rejectDisabled || !slug || selectedDates.length !== 0}
                onClick={() => slug && onReject(slug)}
              >
                {rejectPending ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Rejecting…
                  </>
                ) : (
                  "Reject"
                )}
              </Button>
            ) : null}
          </Col>
        </Row>
      </div>

      <div className="p-3">
        <Button
          color="link"
          size="sm"
          className="p-0 fw-semibold"
          onClick={() =>
            onViewProfile?.({ ...worker, ...worker?.user, worker: worker })
          }
        >
          View profile
        </Button>
      </div>
    </div>
  );
}

/**
 * @param {{ pattern: object, shiftIndex: number }[]} shiftEntries
 */
export default function SuggestedWorkersStep1View({
  shiftEntries,
  showReject = true,
  inviteLayout = "global",
  byShift,
  selected,
  inviteListByShift,
  inviteListSessionError,
  loadMore,
  loadMorePending,
  initialPending,
  toggleDate,
  selectAllDatesForWorker,
  addToInviteList,
  removeFromInviteList,
  rejectWorker,
  inviteWorkers,
  inviteWorkersForShift,
  pendingInviteShiftIndex = null,
  inviteMutation,
  listBusy,
  pendingAppendKey,
  pendingRejectKey,
  pendingRemoveKey,
  shiftInviteMeta,
  sendInvitesLabel = "Invite workers",
  showRunSmartMatching = false,
  onRunSmartMatchingForShift,
  pendingSmartMatchShiftIndex = null,
  startSmartMatchingMutation,
}) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);

  const inviteListLoading = inviteListByShift === null;

  const canInviteGlobal =
    inviteLayout === "global" &&
    !inviteListLoading &&
    !!inviteListByShift &&
    shiftEntries.some(
      ({ shiftIndex, pattern }) =>
        pattern?.isFirstReplacementInvited !== true &&
        (inviteListByShift[shiftIndex]?.workers?.length ?? 0) > 0
    );

  const hideSendInvitesButton =
    inviteLayout === "global" &&
    shiftEntries.length > 0 &&
    shiftEntries.every(({ pattern }) => pattern?.isFirstReplacementInvited === true);

  const openProfile = (user) => {
    setProfileUser(user);
    setIsProfileOpen(true);
  };

  return (
    <>
      {inviteListSessionError && (
        <Alert color="warning" className="mb-3">
          Could not load your invite list session. You can still add workers;
          refresh if the list looks wrong.
        </Alert>
      )}

      {shiftEntries.map(({ pattern, shiftIndex }) => {
        if (pattern?.isFirstReplacementInvited === true) {
          return (
            <ShiftSection
              key={shiftIndex}
              shiftIndex={shiftIndex}
              pattern={pattern}
            >
              <Alert color="success" className="mb-0">
                Replacement invites have already been sent for this shift.
              </Alert>
            </ShiftSection>
          );
        }

        const row = byShift[shiftIndex] || {};
        const automaticSmartMatchingRunning =
          pattern?.invitedOrSmartMatchingStarted === true ||
          pattern?.hasSmartMatchingRunning === true ||
          row.automaticSmartMatchingRunning === true;

        if (showRunSmartMatching && automaticSmartMatchingRunning) {
          return (
            <ShiftSection
              key={shiftIndex}
              shiftIndex={shiftIndex}
              pattern={pattern}
            >
              <Alert color="info" className="d-flex align-items-center gap-2 mb-0">
                <Spinner size="sm" />
                Your selection time has ended. Automatic smart matching is running
                for this shift. Suggested workers will appear here when matching
                completes.
              </Alert>
            </ShiftSection>
          );
        }

        const workersAll = row.workers ?? [];
        const shiftInvite = inviteListByShift?.[shiftIndex];
        const workers = workersAll.filter(
          (w) => !isSuggestedWorkerAlreadyInvited(w, shiftInvite)
        );
        const hasMore = row.hasMore;
        const matchingStatus = row.matchingStatus;
        const sessionError = row.sessionError;
        const suggestedFetchError = row.suggestedFetchError;
        const initialLoaded = row.initialLoaded;
        const loadingInit =
          initialPending[shiftIndex] === true &&
          !initialLoaded &&
          !sessionError &&
          !suggestedFetchError;
        const lm = loadMorePending[shiftIndex];
        const selRow = selected[shiftIndex] || {};
        const shiftMeta = shiftInviteMeta?.[shiftIndex];
        const shiftFull = !!shiftMeta?.isFull;

        return (
          <ShiftSection
            key={shiftIndex}
            shiftIndex={shiftIndex}
            pattern={pattern}
          >
            {matchingStatus === "processing" && !workersAll.length && (
              <Alert color="info" className="d-flex align-items-center gap-2">
                <Spinner size="sm" /> Finding suggested workers…
              </Alert>
            )}

            {sessionError && (
              <Alert color="danger">
                Could not load your saved suggestions session. Refresh the page
                or contact support if this continues.
              </Alert>
            )}

            {suggestedFetchError && !sessionError && (
              <Alert color="danger">
                Could not load suggested workers for this shift.
              </Alert>
            )}

            {shiftFull && (
              <Alert color="warning" className="py-2">
                You have reached the maximum invite limit for this shift. Remove
                someone from the invite list before adding more workers.
              </Alert>
            )}

            {loadingInit &&
              !workersAll.length &&
              matchingStatus !== "processing" && (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              )}

            {!loadingInit &&
              !workers.length &&
              matchingStatus !== "processing" &&
              !sessionError &&
              !suggestedFetchError && (
                <p className="text-muted mb-0">
                  {workersAll.length > 0
                    ? "Suggested workers here are already invited."
                    : "No suggested workers yet. Try again later or load more."}
                </p>
              )}

            {!!workers.length && (
              <Row className="g-3">
                {workers.map((worker, wi) => {
                  const slug = getWorkerSlug(worker);
                  const selectedDates = (slug && selRow[slug]) || [];
                  const actionKey = slug ? getStep1ActionKey(shiftIndex, slug) : "";
                  return (
                    <Col key={slug || wi} xs={12} md={6} xl={4}>
                      <WorkerRow
                        worker={worker}
                        selectedDates={selectedDates}
                        onToggleDate={(iso) =>
                          slug && toggleDate(shiftIndex, slug, iso)
                        }
                        onSelectAllDatesForWorker={() =>
                          slug && selectAllDatesForWorker(shiftIndex, slug)
                        }
                        onAddToInviteList={(s, workerDates) =>
                          addToInviteList(shiftIndex, s, workerDates)
                        }
                        onReject={(s) =>
                          showReject && rejectWorker && rejectWorker(shiftIndex, s)
                        }
                        addDisabled={
                          listBusy || !slug || !selectedDates.length
                        }
                        addPending={!!slug && pendingAppendKey === actionKey}
                        rejectDisabled={listBusy || !slug}
                        rejectPending={!!slug && pendingRejectKey === actionKey}
                        idPrefix={`s${shiftIndex}-w${wi}`}
                        onViewProfile={openProfile}
                        showReject={showReject}
                      />
                    </Col>
                  );
                })}
              </Row>
            )}

            <InviteListPanel
              shiftIndex={shiftIndex}
              shiftInvite={shiftInvite}
              onRemove={(workerSlug) =>
                removeFromInviteList(shiftIndex, workerSlug)
              }
              removeDisabled={listBusy}
              pendingRemoveKey={pendingRemoveKey}
              inviteListLoading={inviteListLoading}
              shiftCap={shiftMeta?.cap ?? 0}
              shiftCurrentCount={shiftMeta?.currentCount ?? 0}
            />

            <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
              {hasMore && workersAll.length > 0 && (
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  disabled={lm}
                  onClick={() => loadMore(shiftIndex)}
                >
                  {lm ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Loading…
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              )}
            </div>

            {inviteLayout === "perShift" && typeof inviteWorkersForShift === "function" ? (
              <div className="d-flex flex-wrap gap-2 justify-content-md-end mt-3 pt-3 border-top">
                {showRunSmartMatching && typeof onRunSmartMatchingForShift === "function" ? (
                  <Button
                    color="secondary"
                    outline
                    size="lg"
                    className="px-4"
                    disabled={
                      inviteListLoading ||
                      listBusy ||
                      startSmartMatchingMutation?.isPending ||
                      matchingStatus === "processing" ||
                      (shiftInvite?.workers?.length ?? 0) > 0
                    }
                    onClick={() => onRunSmartMatchingForShift(shiftIndex)}
                  >
                    {startSmartMatchingMutation?.isPending &&
                    pendingSmartMatchShiftIndex === shiftIndex ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Starting…
                      </>
                    ) : (
                      "Run Smart Matching"
                    )}
                  </Button>
                ) : null}
                <Button
                  color="primary"
                  size="lg"
                  className="px-4"
                  disabled={
                    inviteListLoading ||
                    listBusy ||
                    inviteMutation.isPending ||
                    startSmartMatchingMutation?.isPending ||
                    !shiftInvite ||
                    !(shiftInvite.workers?.length > 0) ||
                    !!shiftInvite.workerAlreadyInvited
                  }
                  onClick={() => inviteWorkersForShift(shiftIndex)}
                >
                  {inviteMutation.isPending && pendingInviteShiftIndex === shiftIndex ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Sending…
                    </>
                  ) : (
                    sendInvitesLabel
                  )}
                </Button>
              </div>
            ) : null}
          </ShiftSection>
        );
      })}

      {inviteLayout === "global" && !hideSendInvitesButton ? (
        <div className="d-grid d-md-flex justify-content-md-end mt-4 mb-2">
          <Button
            color="primary"
            size="lg"
            className="px-4"
            disabled={inviteMutation.isPending || !canInviteGlobal}
            onClick={() => inviteWorkers?.()}
          >
            {inviteMutation.isPending ? (
              <>
                <Spinner size="sm" className="me-2" /> Inviting…
              </>
            ) : (
              sendInvitesLabel
            )}
          </Button>
        </div>
      ) : null}

      <Modal isOpen={isProfileOpen} toggle={() => setIsProfileOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsProfileOpen(false)}>
          Worker profile
        </ModalHeader>
        <ModalBody>{profileUser ? <WorkerMiniProfile user={profileUser} /> : null}</ModalBody>
      </Modal>
    </>
  );
}
